import React, { useEffect, useState } from "react";
import { Heart } from "lucide-react";

const STORAGE_KEY = "ts_beta_ack_v888";

/**
 * BetaBanner — cinematic modal shown once per browser session.
 * Announces the site is in beta v8.88, dark navy + gold aesthetic.
 */
export const BetaBanner = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // slight delay so page paints first
        const t = setTimeout(() => setOpen(true), 900);
        return () => clearTimeout(t);
      }
    } catch { /* localStorage blocked — show anyway */
      setOpen(true);
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="beta-banner"
      className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        onClick={dismiss}
        className="absolute inset-0 bg-[#050914]/85 backdrop-blur-sm animate-in fade-in duration-300"
      />

      {/* Panel */}
      <div className="relative w-full max-w-md rounded-xl overflow-hidden border border-[#D4A63D]/40 shadow-[0_30px_90px_rgba(0,0,0,0.7)] animate-in fade-in zoom-in-95 duration-500">
        {/* Ambient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128] via-[#0F1830] to-[#050914]" />
        <div className="absolute -top-24 -left-16 w-64 h-64 rounded-full bg-[#D4A63D]/15 blur-3xl" />
        <div className="absolute -bottom-24 -right-16 w-72 h-72 rounded-full bg-[#5FA0E8]/10 blur-3xl" />

        {/* Content */}
        <div className="relative px-6 py-8 sm:px-10 sm:py-10 text-center">
          {/* Version pill */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md border border-[#D4A63D]/60 bg-[#D4A63D]/10 backdrop-blur-sm shadow-[0_2px_20px_rgba(212,166,61,0.25)]">
            <span className="text-[10px] tracking-[0.35em] font-bold text-[#D4A63D] uppercase">
              Versiunea Beta 8.88
            </span>
          </div>

          {/* Title */}
          <h2 className="mt-6 font-display text-3xl sm:text-4xl leading-[1.05] tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-[#F2E1B0] via-[#D4A63D] to-[#8F6E1E]">
            ACEASTĂ<br />PAGINĂ ESTE<br />ÎN CONSTRUCȚIE
          </h2>

          {/* Divider with heart */}
          <div className="flex items-center gap-3 my-6 justify-center">
            <span className="h-px w-14 bg-gradient-to-r from-transparent to-[#D4A63D]/60" />
            <Heart className="w-4 h-4 text-[#D4A63D]" />
            <span className="h-px w-14 bg-gradient-to-l from-transparent to-[#D4A63D]/60" />
          </div>

          {/* Brand */}
          <div className="font-display text-2xl sm:text-3xl text-transparent bg-clip-text bg-gradient-to-b from-[#F2E1B0] to-[#D4A63D]">
            Tati și Sofia
          </div>
          <p className="mt-2 text-sm text-[#F8F8F4]/85">
            Pentru copii. Pentru adevăr. Pentru iubire.
          </p>

          {/* Poetic line */}
          <p className="mt-6 text-[13px] text-[#A0AABF] leading-relaxed max-w-xs mx-auto italic">
            Niciun copil nu ar trebui să fie rupt de un părinte care îl iubește.
          </p>

          {/* CTA */}
          <button
            data-testid="beta-banner-dismiss"
            onClick={dismiss}
            className="mt-8 group relative inline-flex items-center justify-center px-8 py-3.5 rounded-md border border-[#D4A63D]/70 bg-gradient-to-b from-[#1B2543] to-[#0F1830] text-[#D4A63D] text-xs font-bold tracking-[0.35em] uppercase transition hover:from-[#D4A63D] hover:to-[#B08A2A] hover:text-[#0A1020] shadow-[0_0_30px_rgba(212,166,61,0.25)]"
          >
            <span className="relative">AM ÎNȚELES</span>
          </button>

          <div className="mt-5 text-[9px] tracking-[0.3em] uppercase text-[#6C7A9C]">
            tatisisofia.ro
          </div>
        </div>
      </div>
    </div>
  );
};
